import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, ArrowRight, Award, Users, Heart, Target, Briefcase, Globe, Shield, TrendingUp, BookOpen, GraduationCap, Compass } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { trackFormSubmission, trackButtonClick } from '../components/GoogleAnalytics';
import StarRating from '../components/StarRating';
import ServicesMarquee from '../components/ServicesMarquee';
import BackToTop from '../components/BackToTop';
import '../styles/AdminReviews.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [expandedCertification, setExpandedCertification] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null); // 'services' | 'shop' | null
  const mobileMenuRef = useRef(null);

  const closeDropdowns = () => setOpenDropdown(null);
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMenuOpen(false);
    closeDropdowns();
  };

  // Reset mobile menu scroll position whenever it opens
  useEffect(() => {
    if (isMenuOpen && mobileMenuRef.current) {
      mobileMenuRef.current.scrollTop = 0;
    }
  }, [isMenuOpen]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewPhoto, setReviewPhoto] = useState(null); // data URL
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  const CALENDLY_URL = 'https://calendly.com/africasabehavioralhealth-info/30min';

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews`);
      if (response.ok) {
        const data = await response.json();
        setReviews(Array.isArray(data.reviews) ? data.reviews : []);
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const certificationDetails = {
    'WOSB': {
      title: 'SBA WOSB — Women-Owned Small Business (Certified)',
      description: 'A federal certification that verifies the business is at least 51% owned and controlled by one or more women who are U.S. citizens. AfriCasa Roots LLC is officially SBA WOSB Certified, giving us full eligibility to compete for federal set-aside contracts reserved for women-owned small businesses.'
    },
    'WBENC': {
      title: "WBENC WBE — Women's Business Enterprise",
      description: "WBENC's Women's Business Enterprise (WBE) certification is the gold standard for women-owned business certification among corporate supplier diversity programs across the country. Certification in progress."
    },
    'MBE': {
      title: 'Vermont M/WBE — Minority/Women-Owned Business',
      description: "Vermont's Minority- and Women-Owned Business certification recognizes qualified businesses for inclusion in state procurement and supplier-diversity programs. Certification pending."
    }
  };

  const toggleCertification = (certType) => {
    setExpandedCertification(expandedCertification === certType ? null : certType);
  };

  // Scroll to a section if URL contains a hash (e.g. /#services after BACK TO HOME)
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      // Wait for layout/render before scrolling
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      // Intersection Observer for scroll animations
      const sections = document.querySelectorAll('[data-animate]');
      sections.forEach(section => {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.8) {
          setVisibleSections(prev => new Set([...prev, section.dataset.animate]));
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
      closeDropdowns();
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Use the global API constant that already includes /api prefix
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '',
        organization: formData.organization || '',
        message: formData.message
      }, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      // Track successful form submission
      trackFormSubmission('contact_form');
      
      toast.success('Thank you! We\'ll be in touch soon.');
      setFormData({ name: '', email: '', phone: '', organization: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error('Something went wrong. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center">
            <div className="logo-container md:mr-12 lg:mr-16">
              <img
                src="https://customer-assets.emergentagent.com/job_pro-landing-page-21/artifacts/3l62dgff_AZ2m47AJpnyV8G0AjPPNlg-AZ2m47AJNCCxNcItlk4hCA.png"
                alt="AfriCasa Logo"
                className="logo"
              />
            </div>

            {/* Desktop Navigation — fills remaining width, items evenly distributed end-to-end (CONTACT pinned right) */}
            <div className="hidden md:flex flex-1 items-center justify-between">
              <button onClick={scrollToTop} className="nav-link" data-testid="nav-home-btn">HOME</button>
              <button onClick={() => scrollToSection('about')} className="nav-link" data-testid="nav-about-btn">ABOUT</button>
              <button onClick={() => scrollToSection('who-we-serve')} className="nav-link" data-testid="nav-who-we-serve-btn">WHO WE SERVE</button>
              <div
                className={`nav-resources-wrapper ${openDropdown === 'services' ? 'dropdown-open' : ''}`}
                onMouseEnter={() => setOpenDropdown('services')}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button
                  className="nav-link nav-link-with-caret"
                  data-testid="nav-services-btn"
                  onClick={() => setOpenDropdown(openDropdown === 'services' ? null : 'services')}
                  aria-expanded={openDropdown === 'services'}
                >
                  SERVICES
                  <span className="nav-caret" aria-hidden="true">▾</span>
                </button>
                <div className="nav-resources-dropdown" role="menu" data-testid="nav-services-dropdown">
                  <Link to="/cultural-needs-assessment" onClick={closeDropdowns} className="nav-resources-item" role="menuitem" data-testid="nav-services-cna">Cultural Needs Assessment</Link>
                  <Link to="/cultural-competency-consulting" onClick={closeDropdowns} className="nav-resources-item" role="menuitem" data-testid="nav-services-ccc">Cultural Competency Consulting</Link>
                  <Link to="/case-management" onClick={closeDropdowns} className="nav-resources-item" role="menuitem" data-testid="nav-services-cm">Organizational Case Management</Link>
                  <Link to="/concierge-case-management" onClick={closeDropdowns} className="nav-resources-item" role="menuitem" data-testid="nav-services-ccm">Concierge Case Management</Link>
                  <Link to="/culturally-responsive-coaching" onClick={closeDropdowns} className="nav-resources-item" role="menuitem" data-testid="nav-services-dwc">Culturally Responsive Coaching</Link>
                  <Link to="/services" onClick={closeDropdowns} className="nav-resources-item nav-resources-item-all" role="menuitem" data-testid="nav-services-all">All Services</Link>
                </div>
              </div>
              <div
                className={`nav-resources-wrapper ${openDropdown === 'certifications' ? 'dropdown-open' : ''}`}
                onMouseEnter={() => setOpenDropdown('certifications')}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button
                  className="nav-link nav-link-with-caret"
                  data-testid="nav-certifications-btn"
                  onClick={() => setOpenDropdown(openDropdown === 'certifications' ? null : 'certifications')}
                  aria-expanded={openDropdown === 'certifications'}
                >
                  CERTIFICATIONS
                  <span className="nav-caret" aria-hidden="true">▾</span>
                </button>
                <div className="nav-resources-dropdown" role="menu" data-testid="nav-certifications-dropdown">
                  <button
                    onClick={() => { scrollToSection('certifications'); }}
                    className="nav-resources-item nav-resources-item-cert"
                    role="menuitem"
                    data-testid="nav-cert-wosb"
                  >
                    <span>SBA WOSB</span>
                    <span className="nav-cert-status">Certified</span>
                  </button>
                  <button
                    onClick={() => { scrollToSection('certifications'); }}
                    className="nav-resources-item nav-resources-item-cert"
                    role="menuitem"
                    data-testid="nav-cert-wbenc"
                  >
                    <span>WBENC WBE</span>
                    <span className="nav-cert-status">In Progress</span>
                  </button>
                  <button
                    onClick={() => { scrollToSection('certifications'); }}
                    className="nav-resources-item nav-resources-item-cert"
                    role="menuitem"
                    data-testid="nav-cert-mbe"
                  >
                    <span>Vermont M/WBE</span>
                    <span className="nav-cert-status">Pending</span>
                  </button>
                </div>
              </div>
              <button onClick={() => scrollToSection('reviews')} className="nav-link" data-testid="nav-reviews-btn">REVIEWS</button>
              <div
                className={`nav-resources-wrapper ${openDropdown === 'shop' ? 'dropdown-open' : ''}`}
                onMouseEnter={() => setOpenDropdown('shop')}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button
                  className="nav-link nav-link-with-caret"
                  data-testid="nav-resources-btn"
                  onClick={() => setOpenDropdown(openDropdown === 'shop' ? null : 'shop')}
                  aria-expanded={openDropdown === 'shop'}
                >
                  SHOP
                  <span className="nav-caret" aria-hidden="true">▾</span>
                </button>
                <div className="nav-resources-dropdown" role="menu" data-testid="nav-resources-dropdown">
                  <Link to="/free-starter-guide" onClick={closeDropdowns} className="nav-resources-item" role="menuitem" data-testid="nav-resources-free-guide">Free Starter Guide</Link>
                  <Link to="/culturally-responsive-practice-guide" onClick={closeDropdowns} className="nav-resources-item" role="menuitem" data-testid="nav-resources-ebook">The Practice Guide</Link>
                </div>
              </div>
              <button onClick={() => scrollToSection('contact')} className="nav-link" data-testid="nav-contact-btn">CONTACT</button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-cream ml-auto"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden mobile-menu" ref={mobileMenuRef}>
              <button onClick={scrollToTop} className="mobile-nav-link" data-testid="mobile-nav-home">HOME</button>
              <button onClick={() => scrollToSection('about')} className="mobile-nav-link">ABOUT</button>
              <button onClick={() => scrollToSection('who-we-serve')} className="mobile-nav-link">WHO WE SERVE</button>

              <div className="mobile-nav-link mobile-nav-group-label">SERVICES</div>
              <Link to="/cultural-needs-assessment" className="mobile-nav-link mobile-nav-sublink" onClick={() => setIsMenuOpen(false)}>— Cultural Needs Assessment</Link>
              <Link to="/cultural-competency-consulting" className="mobile-nav-link mobile-nav-sublink" onClick={() => setIsMenuOpen(false)}>— Cultural Competency Consulting</Link>
              <Link to="/case-management" className="mobile-nav-link mobile-nav-sublink" onClick={() => setIsMenuOpen(false)}>— Organizational Case Management</Link>
              <Link to="/concierge-case-management" className="mobile-nav-link mobile-nav-sublink" onClick={() => setIsMenuOpen(false)}>— Concierge Case Management</Link>
              <Link to="/culturally-responsive-coaching" className="mobile-nav-link mobile-nav-sublink" onClick={() => setIsMenuOpen(false)}>— Culturally Responsive Coaching</Link>
              <Link to="/services" className="mobile-nav-link mobile-nav-sublink" onClick={() => setIsMenuOpen(false)}>— All Services</Link>

              <div className="mobile-nav-link mobile-nav-group-label">CERTIFICATIONS</div>
              <button onClick={() => { scrollToSection('certifications'); }} className="mobile-nav-link mobile-nav-sublink" data-testid="mobile-nav-cert-wosb">— SBA WOSB <span className="nav-cert-status">Certified</span></button>
              <button onClick={() => { scrollToSection('certifications'); }} className="mobile-nav-link mobile-nav-sublink" data-testid="mobile-nav-cert-wbenc">— WBENC WBE <span className="nav-cert-status">In Progress</span></button>
              <button onClick={() => { scrollToSection('certifications'); }} className="mobile-nav-link mobile-nav-sublink" data-testid="mobile-nav-cert-mbe">— Vermont M/WBE <span className="nav-cert-status">Pending</span></button>

              <button onClick={() => scrollToSection('reviews')} className="mobile-nav-link">REVIEWS</button>

              <div className="mobile-nav-link mobile-nav-group-label">SHOP</div>
              <Link to="/free-starter-guide" className="mobile-nav-link mobile-nav-sublink" onClick={() => setIsMenuOpen(false)} data-testid="mobile-nav-free-guide">— Free Starter Guide</Link>
              <Link to="/culturally-responsive-practice-guide" className="mobile-nav-link mobile-nav-sublink" onClick={() => setIsMenuOpen(false)} data-testid="mobile-nav-ebook">— The Practice Guide</Link>

              <button onClick={() => scrollToSection('contact')} className="mobile-nav-link">CONTACT</button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section" data-animate="hero">
        {/* Video Background */}
        <video className="hero-video-bg" autoPlay loop muted playsInline poster="">
          <source src="https://videos.pexels.com/video-files/7579074/7579074-uhd_2732_1440_25fps.mp4" type="video/mp4" />
          <source src="https://cdn.coverr.co/videos/coverr-african-american-woman-therapist-smiling-9544/1080p.mp4" type="video/mp4" />
        </video>
        <div className="hero-video-overlay"></div>
        
        <div className={`container px-6 fade-in ${visibleSections.has('hero') ? 'visible' : ''}`}>
          <div className="hero-grid">
            <div className="hero-text">
              <h1 className="hero-title-main">
                Where Clinical Excellence Meets <span className="text-gold glowy-text">Cultural Roots</span>
              </h1>
              <p className="hero-description">
                Specialized behavioral health consulting for organizations serving refugee and immigrant communities across Rhode Island and Vermont.
              </p>
              <div className="hero-buttons">
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold"
                  onClick={() => trackButtonClick('Book a Discovery Call', 'hero_section')}
                  data-testid="hero-book-consultation-btn"
                >
                  Book a Discovery Call
                </a>
                <a 
                  href="tel:+18024885307" 
                  className="btn-outline"
                  onClick={() => trackButtonClick('Call Phone', 'hero_section')}
                >
                  <Phone size={18} />
                  Call (802) 488-5307
                </a>
              </div>
            </div>

            <div className="hero-stats">
              <div className="stat-card">
                <blockquote className="quote-handwriting">
                  Every community deserves care that speaks their language — not just in words, but in understanding.
                </blockquote>
                <p className="hero-experience-text">
                  Bringing over a decade of specialized experience in behavioral health, trauma-informed care, and culturally responsive mental health services to refugee and immigrant communities.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section" data-animate="mission">
        <div className={`container mx-auto px-6 fade-in ${visibleSections.has('mission') ? 'visible' : ''}`}>
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="unified-title">Our Mission</h2>
              <p className="section-description">
                To provide culturally responsive behavioral health consulting that honors the lived experiences of marginalized communities.
              </p>
              
              {/* Mission Images - SWAPPED */}
              <div className="mission-images">
                <div className="mission-image">
                  <img src="https://customer-assets.emergentagent.com/job_pro-landing-page-21/artifacts/4dk3hcx0_pexels-cottonbro-7609958.jpg" alt="Black woman professional reading" />
                </div>
                <div className="mission-image">
                  <img src="https://customer-assets.emergentagent.com/job_pro-landing-page-21/artifacts/hecgso0y_IMG_0885.jpeg" alt="Community event with African attire" />
                </div>
              </div>
            </div>

            <div className="values-grid">
              <div className="value-card">
                <Heart className="value-icon" size={32} />
                <h3 className="value-title">Culturally Rooted</h3>
                <p className="value-description">Care grounded in cultural understanding and lived experience</p>
              </div>
              <div className="value-card">
                <Shield className="value-icon" size={32} />
                <h3 className="value-title">Clinically Grounded</h3>
                <p className="value-description">Evidence-based practices with clinical rigor</p>
              </div>
              <div className="value-card">
                <Target className="value-icon" size={32} />
                <h3 className="value-title">Dignity</h3>
                <p className="value-description">Every person deserves respectful, compassionate care</p>
              </div>
              <div className="value-card">
                <TrendingUp className="value-icon" size={32} />
                <h3 className="value-title">Innovation</h3>
                <p className="value-description">Modern solutions for evolving community needs</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why AfriCasa Section */}
      <section className="why-section" data-animate="why">
        <div className={`container mx-auto px-6 fade-in ${visibleSections.has('why') ? 'visible' : ''}`}>
          <div className="text-center mb-16">
            <h2 className="unified-title">Why Partner with AfriCasa?</h2>
            <p className="section-subtitle">What sets us apart in behavioral health consulting</p>
          </div>

          <div className="why-grid">
            <div className="why-card">
              <h3 className="why-title">We Are the Community We Serve</h3>
              <p className="why-description">
                Our cultural competence isn&apos;t theoretical — it&apos;s lived. We bring authentic understanding of the refugee and immigrant experience.
              </p>
            </div>

            <div className="why-card">
              <h3 className="why-title">Clinical Depth + Business Agility</h3>
              <p className="why-description">
                Licensed clinical expertise combined with business acumen to deliver solutions that are both effective and sustainable.
              </p>
            </div>

            <div className="why-card">
              <h3 className="why-title">Multilingual Capacity</h3>
              <p className="why-description">
                Access to behavioral health professionals fluent in multiple languages, ensuring truly accessible care.
              </p>
            </div>

            <div className="why-card">
              <h3 className="why-title">Woman-Owned Business</h3>
              <p className="why-description">
                SBA WOSB Certified. WBENC WBE and Vermont M/WBE certifications in progress — committed to diversity in healthcare leadership and service delivery.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="who-we-serve" className="who-serve-section" data-animate="who-serve">
        <div className={`container mx-auto px-6 fade-in ${visibleSections.has('who-serve') ? 'visible' : ''}`}>
          <div className="text-center mb-16">
            <h2 className="unified-title">Who We Serve</h2>
            <p className="section-subtitle">Partnering with organizations and empowering individuals and families through culturally responsive consulting, coaching, and support services.</p>
          </div>

          <div className="who-serve-grid who-serve-grid-6">
            <div className="serve-card">
              <div className="serve-card-image">
                <img src="https://images.unsplash.com/photo-1683650666910-ecc317a12061" alt="Hospitals and health systems" />
              </div>
              <Briefcase className="serve-icon" size={40} />
              <h3 className="serve-title">Hospitals & Health Systems</h3>
              <p className="serve-description">
                Cultural needs assessments, organizational case management, staff training, and culturally responsive care initiatives.
              </p>
            </div>

            <div className="serve-card">
              <div className="serve-card-image">
                <img src="https://images.unsplash.com/photo-1580582932707-520aed937b7b" alt="Schools and educational institutions" />
              </div>
              <GraduationCap className="serve-icon" size={40} />
              <h3 className="serve-title">Schools & Educational Institutions</h3>
              <p className="serve-description">
                Training, consultation, and program development that strengthens support for refugee, immigrant, and multicultural students.
              </p>
            </div>

            <div className="serve-card">
              <div className="serve-card-image">
                <img src="https://images.unsplash.com/photo-1641057324798-c05769de628a" alt="Refugee resettlement and community organizations" />
              </div>
              <Users className="serve-icon" size={40} />
              <h3 className="serve-title">Refugee Resettlement & Community Organizations</h3>
              <p className="serve-description">
                Capacity building, organizational case management, culturally responsive consulting, and community partnership support.
              </p>
            </div>

            <div className="serve-card">
              <div className="serve-card-image">
                <img src="https://images.pexels.com/photos/7298844/pexels-photo-7298844.jpeg" alt="Government agencies and nonprofits" />
              </div>
              <Globe className="serve-icon" size={40} />
              <h3 className="serve-title">Government Agencies & Nonprofits</h3>
              <p className="serve-description">
                Policy consultation, program development, organizational case management, and culturally responsive implementation strategies.
              </p>
            </div>

            <div className="serve-card">
              <div className="serve-card-image">
                <img src="https://images.unsplash.com/photo-1518495973542-4542c06a5843" alt="Individuals and families" />
              </div>
              <Heart className="serve-icon" size={40} />
              <h3 className="serve-title">Individuals & Families</h3>
              <p className="serve-description">
                Personalized case management, coaching, and culturally responsive support that helps individuals and families navigate healthcare, behavioral health, education, employment, housing, and other life transitions.
              </p>
            </div>

            <div className="serve-card">
              <div className="serve-card-image">
                <img src="https://images.unsplash.com/photo-1531058020387-3be344556be6" alt="African diaspora and immigrant communities" />
              </div>
              <Compass className="serve-icon" size={40} />
              <h3 className="serve-title">African Diaspora & Immigrant Communities</h3>
              <p className="serve-description">
                Culturally responsive wellness coaching and personalized guidance that empowers members of the African diaspora, immigrants, and refugees to build confidence, navigate life transitions, and thrive across cultures.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section services-with-bg" data-animate="services">
        <div className="services-bg-image-full">
          <img src="https://customer-assets.emergentagent.com/job_pro-landing-page-21/artifacts/kd7rzvmy_pexels-tima-miroshnichenko-5717194.jpg" alt="Professional in office" />
        </div>
        <div className="services-video-overlay"></div>
        
        <div className={`container mx-auto px-6 fade-in ${visibleSections.has('services') ? 'visible' : ''}`}>
          <div className="text-center mb-16">
            <h2 className="unified-title">Services We Offer</h2>
            <p className="section-subtitle">Culturally responsive consulting and coaching services for organizations and individuals.</p>
          </div>

          {/* ORGANIZATIONS group — Consulting Services */}
          <div className="services-group services-group-container" data-testid="services-group-organizations">
            <div className="services-group-header">
              <h3 className="services-group-title">Consulting Services</h3>
              <p className="services-group-description">
                Helping organizations build culturally responsive systems through strategic consulting, cultural needs assessments, organizational case management, and capacity-building solutions that strengthen programs, improve service delivery, and create meaningful impact for the communities they serve.
              </p>
            </div>
            <div className="services-grid">
              <div className="service-card service-card-static" id="service-cultural-needs-assessment" data-testid="service-card-1">
                <div className="service-number">01</div>
                <h3 className="service-title">Cultural Needs Assessment</h3>
                <p className="service-description">
                  We conduct rapid data driven assessments that identify your organization&apos;s cultural competency gaps and deliver a clear actionable roadmap for serving refugee and immigrant communities with greater precision and effectiveness.
                </p>
                <div className="service-card-actions">
                  <Link to="/cultural-needs-assessment" className="service-click-hint service-click-hint-link" data-testid="service-card-1-learn-more">
                    Learn More
                  </Link>
                </div>
              </div>

              <div className="service-card service-card-static" id="service-cultural-competency-consulting" data-testid="service-card-2">
                <div className="service-number">02</div>
                <h3 className="service-title">Cultural Competency Consulting</h3>
                <p className="service-description">
                  We partner with your organization to build the cultural frameworks, staff training, systems, and programs needed to truly serve refugee and immigrant communities with precision and dignity. From audit to implementation we walk alongside your organization every step of the way.
                </p>
                <div className="service-card-actions">
                  <Link to="/cultural-competency-consulting" className="service-click-hint service-click-hint-link" data-testid="service-card-2-learn-more">
                    Learn More
                  </Link>
                </div>
              </div>

              <div className="service-card service-card-static" id="service-case-management" data-testid="service-card-3">
                <div className="service-number">03</div>
                <h3 className="service-title">Organizational Case Management Services</h3>
                <p className="service-description">
                  We partner with organizations to strengthen case management systems through culturally responsive care coordination, capacity building, resource navigation, and client advocacy, ensuring individuals and families receive seamless, high-quality support.
                </p>
                <div className="service-card-actions">
                  <Link to="/case-management" className="service-click-hint service-click-hint-link" data-testid="service-card-3-learn-more">
                    Learn More
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Divider between groups is removed — the two premium containers naturally separate the categories */}

          {/* COACHING SERVICES group */}
          <div className="services-group services-group-container" data-testid="services-group-individuals">
            <div className="services-group-header">
              <h3 className="services-group-title">Coaching &amp; Concierge Services</h3>
              <p className="services-group-description">
                Offering one-on-one culturally responsive coaching tailored for immigrants, refugees, and members of the African diaspora, alongside concierge case management services for individuals and families seeking personalized guidance navigating healthcare, behavioral health, housing, education, employment, and community resources. Together, these services provide practical support, advocacy, and personalized solutions to help clients move forward with confidence.
              </p>
            </div>

            <div className="services-grid services-grid-two">
              <div className="service-card service-card-static" id="service-culturally-responsive-coaching" data-testid="service-card-4">
                <div className="service-number">04</div>
                <h3 className="service-title">Culturally Responsive Coaching</h3>
                <p className="service-description">
                  One-on-one culturally responsive coaching tailored to the unique goals, lived experiences, and cultural backgrounds of immigrants, refugees, and members of the African diaspora. We help clients navigate life across cultures, build resilience, and achieve their personal and professional goals with confidence.
                </p>
                <div className="service-card-actions">
                  <Link to="/culturally-responsive-coaching" className="service-click-hint service-click-hint-link" data-testid="service-card-4-learn-more">
                    Learn More
                  </Link>
                </div>
              </div>

              <div className="service-card service-card-static" id="service-concierge-case-management" data-testid="service-card-5">
                <div className="service-number">05</div>
                <h3 className="service-title">Concierge Case Management Services</h3>
                <p className="service-description">
                  Personalized, one-on-one case management designed to help individuals and families confidently navigate healthcare, behavioral health, housing, education, employment, and community resources through compassionate, culturally responsive support.
                </p>
                <div className="service-card-actions">
                  <Link to="/concierge-case-management" className="service-click-hint service-click-hint-link" data-testid="service-card-5-learn-more">
                    Learn More
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="services-footer">
            <h2 className="unified-title services-cta-heading" data-testid="services-cta-heading">Not Sure Which Service Is Right For You?</h2>
            <p className="services-cta-copy" data-testid="services-cta-copy">
              Whether you&apos;re an organization seeking culturally responsive consulting or an individual looking for culturally responsive coaching or concierge case management, we&apos;re here to help. Schedule a service consultation to discuss your goals and determine which service best fits your needs.
            </p>
            <a
              href="https://calendly.com/africasabehavioralhealth-info/service-consultation"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-consultation"
              data-testid="services-book-consultation-btn"
            >
              Book a Service Consultation
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      {/* About/Founder Section */}
      <section id="about" className="about-section" data-animate="about">
        <div className={`container mx-auto px-6 fade-in ${visibleSections.has('about') ? 'visible' : ''}`}>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="founder-image">
              <div className="founder-image-wrapper">
                <img 
                  src="https://customer-assets.emergentagent.com/job_pro-landing-page-21/artifacts/16awaqhs_IMG_2295.jpg" 
                  alt="Founder" 
                  className="founder-photo"
                />
              </div>
            </div>

            <div className="founder-bio-container">
              <h2 className="founder-name">Meet The Founder</h2>
              
              <p className="founder-bio-text">
                I am a Licensed Master Social Worker (LMSW) with over 10 years of experience in behavioral health, specializing in trauma informed care and culturally responsive mental health services. My journey is deeply personal as a member of the Black and immigrant community, I have witnessed firsthand the profound gaps in mental health care for refugee and immigrant populations.
              </p>
              
              <p className="founder-bio-text">
                AfriCasa was born from a commitment to bridge these gaps. I hold advanced training in trauma informed care, cultural competency, and program development, and have worked extensively with diverse communities across Rhode Island and Vermont. My lived experience informs every consultation, training session, and partnership.
              </p>
              
              <div className="founder-cta">
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-founder"
                  data-testid="founder-book-consultation-btn"
                >
                  Book a Discovery Call
                  <ArrowRight size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section id="certifications" className="certifications-section certifications-with-bg" data-animate="certifications">
        <div className="certifications-bg-image">
          <img src="https://customer-assets.emergentagent.com/job_pro-landing-page-21/artifacts/br3r1pzs_pexels-criticalimagery-30538156.jpg" alt="Professional at work" />
        </div>
        <div className="certifications-overlay"></div>
        
        <div className={`container mx-auto px-6 fade-in ${visibleSections.has('certifications') ? 'visible' : ''}`}>
          <div className="text-center mb-12">
            <h2 className="unified-title">Our Certifications</h2>
          </div>

          <div className="certifications-content">
            <div className="certification-logo-section">
              <div className="certification-badges-large">
                <div
                  className={`cert-badge-large ${expandedCertification === 'WOSB' ? 'expanded' : ''}`}
                  onClick={() => toggleCertification('WOSB')}
                  style={{ cursor: 'pointer' }}
                  data-testid="cert-card-wosb"
                >
                  <img
                    src="https://customer-assets-lxgj4vgw.emergentagent.net/job_pro-landing-page-21/artifacts/1396c9bc7c1f595d_WOSB%20Certified.png"
                    alt="SBA WOSB Certified — U.S. Small Business Administration"
                    className="cert-badge-image"
                  />
                  <p className="cert-badge-label">Certified Women-Owned Small Business</p>
                  <p className="cert-pending cert-active">SBA Certified</p>
                  {expandedCertification === 'WOSB' && (
                    <div className="cert-explanation">
                      <p>{certificationDetails.WOSB.description}</p>
                    </div>
                  )}
                </div>
                <div
                  className={`cert-badge-large ${expandedCertification === 'WBENC' ? 'expanded' : ''}`}
                  onClick={() => toggleCertification('WBENC')}
                  style={{ cursor: 'pointer' }}
                  data-testid="cert-card-wbenc"
                >
                  <div className="cert-badge-icon">WBENC WBE</div>
                  <p className="cert-badge-label">Women&apos;s Business Enterprise</p>
                  <p className="cert-pending">Certification In Progress</p>
                  {expandedCertification === 'WBENC' && (
                    <div className="cert-explanation">
                      <p>{certificationDetails.WBENC.description}</p>
                    </div>
                  )}
                </div>
                <div
                  className={`cert-badge-large ${expandedCertification === 'MBE' ? 'expanded' : ''}`}
                  onClick={() => toggleCertification('MBE')}
                  style={{ cursor: 'pointer' }}
                  data-testid="cert-card-mbe"
                >
                  <div className="cert-badge-icon">VERMONT M/WBE</div>
                  <p className="cert-badge-label">Minority/Women-Owned Business</p>
                  <p className="cert-pending">Certification Pending</p>
                  {expandedCertification === 'MBE' && (
                    <div className="cert-explanation">
                      <p>{certificationDetails.MBE.description}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="certifications-benefits">
              <h3 className="benefits-title-gold">Why Partner with a Certified Women-Owned Firm?</h3>

              <div className="benefit-item">
                <div className="benefit-icon">
                  <Award size={32} />
                </div>
                <div className="benefit-content">
                  <h4 className="benefit-title">Certified &amp; Procurement-Ready</h4>
                  <p className="benefit-text">
                    AfriCasa Roots LLC is a women-owned behavioral health consulting firm with certifications spanning federal, national, and state procurement programs.
                  </p>
                </div>
              </div>

              <div className="benefit-item">
                <div className="benefit-icon">
                  <Shield size={32} />
                </div>
                <div className="benefit-content">
                  <h4 className="benefit-title">Specialized Expertise</h4>
                  <p className="benefit-text">
                    We combine professional behavioral health expertise with culturally responsive approaches to consulting, training, program support, and organizational development.
                  </p>
                </div>
              </div>

              <div className="benefit-item">
                <div className="benefit-icon">
                  <Heart size={32} />
                </div>
                <div className="benefit-content">
                  <h4 className="benefit-title">Purpose-Driven Partnership</h4>
                  <p className="benefit-text">
                    We partner with organizations seeking thoughtful, culturally responsive solutions that strengthen services and improve outcomes for the communities they serve.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section - Submit & View */}
      <section id="reviews" className="reviews-section" data-animate="reviews">
        <div className="container mx-auto px-6">
          <div className="reviews-header reviews-header-center">
            <h2 className="unified-title reviews-title-center" data-testid="reviews-section-title">Reviews</h2>
            <p className="section-subtitle reviews-subtitle-center">Share your experience working with AfriCasa</p>
          </div>
          
          {/* Review Submission Form */}
          <div className="review-form-container" data-testid="review-form-container">
            <h3 className="review-form-title">Write a Review</h3>
            <form className="review-form" data-testid="review-form" onSubmit={async (e) => {
              e.preventDefault();
              if (reviewSubmitting) return;
              if (!reviewRating) {
                toast.error('Please select a star rating');
                return;
              }
              setReviewSubmitting(true);
              const formData = new FormData(e.target);
              const reviewData = {
                name: formData.get('name'),
                organization: formData.get('organization'),
                rating: reviewRating,
                reviewText: formData.get('reviewText'),
                photo_url: reviewPhoto,
                created_at: new Date().toISOString()
              };

              try {
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(reviewData)
                });

                if (response.ok) {
                  toast.success('Thank you for your review! It will be published after approval.');
                  e.target.reset();
                  setReviewRating(0);
                  setReviewPhoto(null);
                  fetchReviews();
                } else {
                  toast.error('Failed to submit review. Please try again.');
                }
              } catch (error) {
                console.error('Review submission error:', error);
                toast.error('Something went wrong. Please try again.');
              } finally {
                setReviewSubmitting(false);
              }
            }}>
              <div className="form-group">
                <label htmlFor="review-name">Your Name *</label>
                <input
                  type="text"
                  id="review-name"
                  name="name"
                  required
                  className="review-input"
                  data-testid="review-name-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="review-org">Organization</label>
                <input
                  type="text"
                  id="review-org"
                  name="organization"
                  className="review-input"
                  data-testid="review-org-input"
                />
              </div>

              <div className="form-group">
                <label>Rating *</label>
                <StarRating value={reviewRating} onChange={setReviewRating} testIdPrefix="review-star" />
              </div>

              <div className="form-group">
                <label>Photo or Logo (optional)</label>
                <div className="review-photo-uploader">
                  {reviewPhoto ? (
                    <img src={reviewPhoto} alt="" className="review-photo-preview" data-testid="review-photo-preview" />
                  ) : (
                    <div className="review-photo-empty" aria-hidden="true">PNG · JPG</div>
                  )}
                  <div className="review-photo-actions">
                    <label className="review-photo-label" data-testid="review-photo-upload-label">
                      {reviewPhoto ? 'Change' : 'Upload'}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        style={{ display: 'none' }}
                        data-testid="review-photo-input"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 500 * 1024) {
                            toast.error('Image must be smaller than 500KB');
                            e.target.value = '';
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = () => setReviewPhoto(reader.result);
                          reader.readAsDataURL(file);
                        }}
                      />
                    </label>
                    <span className="review-photo-hint">Max 500KB · headshot or logo</span>
                    {reviewPhoto && (
                      <button
                        type="button"
                        className="review-photo-remove"
                        onClick={() => setReviewPhoto(null)}
                        data-testid="review-photo-remove"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-group review-textarea-group">
                <label htmlFor="review-text">Your Review *</label>
                <textarea
                  id="review-text"
                  name="reviewText"
                  required
                  rows="5"
                  className="review-textarea"
                  placeholder="Share your experience working with AfriCasa..."
                  data-testid="review-text-textarea"
                ></textarea>
              </div>

              <button type="submit" className="btn-gold" data-testid="review-submit-btn" disabled={reviewSubmitting}>
                {reviewSubmitting ? 'Submitting…' : 'Submit Review'}
              </button>
            </form>
          </div>
          
          <div className="reviews-display" data-testid="reviews-display">
            <h3 className="reviews-display-title">Recent Reviews</h3>
            {reviewsLoading ? (
              <p className="reviews-note">Loading…</p>
            ) : reviews.length === 0 ? (
              <p className="reviews-note">No reviews yet</p>
            ) : (
              <div className="reviews-grid">
                {reviews.slice(0, 6).map((r, idx) => (
                  <div className={`review-card ${r.featured ? 'review-card-featured' : ''}`} key={idx} data-testid={`review-card-${idx}`}>
                    <div className="review-stars">
                      {'★'.repeat(r.rating || 0)}{'☆'.repeat(Math.max(0, 5 - (r.rating || 0)))}
                      {r.featured && <span className="review-featured-badge" data-testid={`review-featured-badge-${idx}`}>★ Featured</span>}
                    </div>
                    <p className="review-text">{r.reviewText}</p>
                    <div className="review-author" style={{ display: 'flex', alignItems: 'center' }}>
                      {r.photo_url && (
                        <img src={r.photo_url} alt={r.name} className="review-card-photo" data-testid={`review-photo-${idx}`} />
                      )}
                      <div className="author-info">
                        <div className="author-name">{r.name}</div>
                        {r.organization && <div className="author-title">{r.organization}</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="cta-section" data-animate="cta">
        {/* Background image */}
        <div className="cta-background-media">
          <img src="https://customer-assets.emergentagent.com/job_pro-landing-page-21/artifacts/r5j3vgqs_pexels-beni-mbuyamba-2159390482-36768584.jpg" alt="Professional consultation" />
        </div>
        
        <div className={`container mx-auto px-6 fade-in ${visibleSections.has('cta') ? 'visible' : ''}`}>
          <div className="text-center mb-12">
            <h2 className="cta-title">Ready to Build Culturally Rooted Care?</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="contact-form-container">
              <h3 className="form-title">Send Us a Message</h3>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input 
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input 
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input 
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="organization">Organization</Label>
                  <Input 
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea 
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="form-input"
                  />
                </div>

                <Button type="submit" className="btn-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <ArrowRight size={18} />
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="contact-info-container">
              <h3 className="form-title">Get In Touch</h3>
              <div className="contact-methods">
                <a href="mailto:info@africasabehavioralhealth.org" className="contact-method">
                  <Mail className="contact-icon" size={24} />
                  <div>
                    <div className="contact-label">Email Us</div>
                    <div className="contact-value">info@africasabehavioralhealth.org</div>
                  </div>
                </a>

                <a href="tel:+18024885307" className="contact-method">
                  <Phone className="contact-icon" size={24} />
                  <div>
                    <div className="contact-label">Call Us</div>
                    <div className="contact-value">(802) 488-5307</div>
                  </div>
                </a>

                <div className="contact-method">
                  <MapPin className="contact-icon" size={24} />
                  <div>
                    <div className="contact-label">Service Area</div>
                    <div className="contact-value">Rhode Island & Vermont</div>
                  </div>
                </div>
              </div>

              <div className="cta-buttons">
                <a href="mailto:info@africasabehavioralhealth.org" className="btn-email">
                  Email Us Directly
                </a>
                <a href="tel:+18024885307" className="btn-phone">
                  Call (802) 488-5307
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container mx-auto px-6">
          <div className="footer-content">
            <div className="footer-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_pro-landing-page-21/artifacts/3l62dgff_AZ2m47AJpnyV8G0AjPPNlg-AZ2m47AJNCCxNcItlk4hCA.png" 
                alt="AfriCasa Logo" 
                className="footer-logo-img"
              />
              <p className="footer-tagline">Culturally Rooted • Clinically Grounded</p>
            </div>

            <div className="footer-links">
              <div>
                <h4 className="footer-heading">Services</h4>
                <ul className="footer-list">
                  <li><Link to="/cultural-needs-assessment">Cultural Needs Assessment</Link></li>
                  <li><Link to="/cultural-competency-consulting">Cultural Competency Consulting</Link></li>
                  <li><Link to="/case-management">Organizational Case Management Services</Link></li>
                  <li><Link to="/concierge-case-management">Concierge Case Management Services</Link></li>
                  <li><Link to="/culturally-responsive-coaching">Culturally Responsive Coaching</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="footer-heading">Company</h4>
                <ul className="footer-list">
                  <li><button onClick={() => scrollToSection('about')}>About</button></li>
                  <li><button onClick={() => scrollToSection('who-we-serve')}>Who We Serve</button></li>
                  <li><button onClick={() => scrollToSection('services')}>Services</button></li>
                  <li><button onClick={() => scrollToSection('certifications')}>Certifications</button></li>
                  <li><button onClick={() => scrollToSection('reviews')}>Reviews</button></li>
                  <li><button onClick={() => scrollToSection('contact')}>Contact</button></li>
                </ul>
              </div>

              <div>
                <h4 className="footer-heading">Contact</h4>
                <ul className="footer-list">
                  <li><a href="mailto:info@africasabehavioralhealth.org">info@africasabehavioralhealth.org</a></li>
                  <li><a href="tel:+18024885307">(802) 488-5307</a></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p className="footer-service-area" data-testid="footer-service-area">
              Based in Vermont · Serving Vermont &amp; Rhode Island · Nationwide opportunities welcome.
            </p>
            <p>© 2026 AfriCasaRoots LLC (DBA AfriCasa Behavioral Health Consulting). All rights reserved.</p>
            <p className="footer-certifications">SBA WOSB Certified • WBENC WBE In Progress • Vermont M/WBE Pending</p>
          </div>
        </div>
      </footer>

      {/* Marquee */}
      <ServicesMarquee />

      <BackToTop />
    </div>
  );
};

export default LandingPage;
